import React, { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import type { SuspensionType } from '@/types/suspension-connector';
import styles from './ChordDropZone.module.css';

interface ChordDropZoneProps {
  /**
   * Unique identifier for this drop zone
   */
  id: string;

  /**
   * ID of the source chord (where suspension preparation is)
   */
  sourceChordId: string;

  /**
   * ID of the target chord (where suspension resolves)
   */
  targetChordId: string;

  /**
   * Visual position of the source chord
   */
  sourcePosition: { x: number; y: number };

  /**
   * Visual position of the target chord
   */
  targetPosition: { x: number; y: number };

  /**
   * Whether a suspension is currently being dragged over the canvas
   */
  isActive: boolean;

  /**
   * Whether the cursor is currently over this drop zone
   */
  isOver: boolean;

  /**
   * Whether this drop zone can accept the suspension being dragged
   */
  isValid: boolean;

  /**
   * Reason why the drop zone is invalid (if applicable)
   */
  invalidReason?: string;

  /**
   * Callback when a suspension is dropped on this zone
   * (Note: actual drop handling typically happens at canvas level)
   */
  onDrop?: (suspensionType: SuspensionType) => void; // Reserved for future use
}

export const ChordDropZone: React.FC<ChordDropZoneProps> = ({
  id,
  sourceChordId,
  targetChordId,
  sourcePosition,
  targetPosition,
  isActive,
  isOver,
  isValid,
  invalidReason,
  onDrop: _onDrop,
}) => {
  // Register this element as droppable with dnd-kit
  const { setNodeRef, isOver: dndKitIsOver } = useDroppable({
    id,
    data: {
      type: 'chord-drop-zone',
      sourceChordId,
      targetChordId,
      isValid,
      invalidReason,
    },
  });

  // Calculate midpoint and line dimensions
  const midpoint = useMemo(() => {
    return {
      x: (sourcePosition.x + targetPosition.x) / 2,
      y: (sourcePosition.y + targetPosition.y) / 2,
    };
  }, [sourcePosition, targetPosition]);

  const distance = useMemo(() => {
    const dx = targetPosition.x - sourcePosition.x;
    const dy = targetPosition.y - sourcePosition.y;
    return Math.sqrt(dx * dx + dy * dy);
  }, [sourcePosition, targetPosition]);

  const angle = useMemo(() => {
    const dx = targetPosition.x - sourcePosition.x;
    const dy = targetPosition.y - sourcePosition.y;
    return Math.atan2(dy, dx) * (180 / Math.PI);
  }, [sourcePosition, targetPosition]);

  // Determine if we should show this drop zone
  const shouldShow = isActive;

  // Determine styling based on state
  const isHovering = isOver || dndKitIsOver;
  const glowColor = isHovering && isValid ? 'success' : isHovering && !isValid ? 'error' : 'neutral';

  if (!shouldShow) {
    return null;
  }

  return (
    <motion.div
      ref={setNodeRef}
      className={styles.dropZoneContainer}
      style={{
        left: midpoint.x,
        top: midpoint.y,
        width: distance,
        height: 2,
        transform: `translate(-50%, -50%) rotate(${angle}deg)`,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: shouldShow ? 1 : 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Main dashed line with pulse animation */}
      <motion.div
        className={`${styles.dashedLine} ${styles[`glow-${glowColor}`]}`}
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Glow effect container */}
      <motion.div
        className={`${styles.glowEffect} ${styles[`glowEffect-${glowColor}`]}`}
        animate={{
          opacity: isHovering ? [0.4, 0.8, 0.4] : [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Tooltip on hover */}
      {isHovering && (
        <motion.div
          className={`${styles.tooltip} ${styles[`tooltip-${glowColor}`]}`}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {isValid ? 'Drop suspension here' : `Invalid: ${invalidReason || 'Unknown reason'}`}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ChordDropZone;
